import React, { useState, useCallback, useEffect } from "react";
import { SourceFieldMapping, SourceFieldMappingView } from "@/entities/all";
import { PlusCircle, GitBranch, ArrowDownCircle, X } from "lucide-react";
import { useLocation } from "react-router-dom";

import MappingsTable from "../components/mappings/MappingsTable";
import CreateMappingModal from "../components/mappings/CreateMappingModal";
import EditMappingModal from "../components/mappings/EditMappingModal";
import PaginatedTable from "../components/ui/paginated-table";
import { FilterSelect, FilterSelectItem } from "@/components/ui/filter-select";

const CONFIDENCE_ITEMS = [
  { value: 'all',     label: 'All Confidence' },
  { value: '0.9-1',   label: 'High (90%+)'    },
  { value: '0.7-0.9', label: 'Medium (70-90%)' },
  { value: '0-0.7',   label: 'Low (<70%)'      },
];

export default function SourceMappings() {
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({ source: 'all', confidence: 'all' });
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingMapping, setEditingMapping] = useState(null);
  const [total, setTotal] = useState(0);
  const [reloadTrigger, setReloadTrigger] = useState(0);
  const [sourceItems, setSourceItems] = useState([{ value: 'all', label: 'All Sources' }]);

  useEffect(() => {
    SourceFieldMappingView.getDistinctValues('source_name', '', 50).then(sources => {
      setSourceItems([
        { value: 'all', label: 'All Sources' },
        ...sources.map(s => ({ value: s, label: s })),
      ]);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (location.state?.sourceFilter) {
      setFilters(prev => ({ ...prev, source: String(location.state.sourceFilter) }));
    }
    if (location.state?.preselectedElement) {
      setIsCreateModalOpen(true);
    }
  }, [location.state]);

  // Live refresh: the Guided demo (and any background mutation) broadcasts
  // `cortex:data-changed` so pages re-fetch and show progress as it happens,
  // instead of showing the stale snapshot from mount.
  useEffect(() => {
    const onChanged = () => setReloadTrigger(prev => prev + 1);
    window.addEventListener("cortex:data-changed", onChanged);
    return () => window.removeEventListener("cortex:data-changed", onChanged);
  }, []);

  const loadData = useCallback(async (page, pageSize) => {
    const offset = (page - 1) * pageSize;
    // Always scope to READ mappings: the unified source_field_mapping table now
    // also holds WRITE rows (element→target bindings, no source field) which are
    // NOT source-field mappings and must never appear on this page.
    const serverFilters = { direction: 'read' };
    if (filters.source !== 'all') serverFilters.source_name = filters.source;
    // Only pass searchColumns alongside an actual search term — passing them with
    // an empty term makes the backend filter match nothing (showed 0 rows).
    const searchColumns = searchTerm ? ['source_field_name', 'source_name', 'element_canonical_name', 'transformation_notes'] : [];

    // We always have at least the direction filter, so use the filter endpoint.
    const response = await SourceFieldMappingView.filterWithTotal(serverFilters, 'source_field_name', pageSize, offset, searchTerm, searchColumns);

    let data = response.data || [];
    if (filters.confidence !== 'all') {
      const [min, max] = filters.confidence.split('-').map(Number);
      data = data.filter(m => {
        const c = m.mapping_confidence || 0;
        return c >= min && c <= max;
      });
    }

    // Fall back to the page's row count if the backend didn't return a total
    // (defensive — never show an empty table when rows came back).
    const total = (response.total != null && !Number.isNaN(response.total))
      ? response.total
      : data.length;
    setTotal(total);
    return { data, total };
  }, [filters, searchTerm]);

  const triggerReload = () => setReloadTrigger(prev => prev + 1);

  const handleMappingUpdated = () => {
    triggerReload();
    setIsCreateModalOpen(false);
    setIsEditModalOpen(false);
    setEditingMapping(null);
  };

  const openEditModal = (mapping) => { setEditingMapping(mapping); setIsEditModalOpen(true); };

  const handleDeleteMapping = async (mappingId) => {
    try {
      await SourceFieldMapping.delete(mappingId);
      triggerReload();
    } catch (error) {
      console.error('Error deleting mapping:', error);
      throw error;
    }
  };

  const exportToCSV = async () => {
    try {
      const allResp = await SourceFieldMappingView.filterWithTotal({ direction: 'read' }, 'source_field_name', 100000, 0);
      const allData = allResp.data || [];
      if (!allData.length) { alert('No mappings to export.'); return; }
      const headers = ['Source Field', 'Data Source', 'Canonical Element', 'Confidence', 'Last Validated'];
      const rows = allData.map((m) => [
        m.source_field_name,
        m.source_name || '',
        m.element_canonical_name || '',
        ((m.mapping_confidence || 0) * 100).toFixed(0) + '%',
        m.last_validated ? new Date(m.last_validated).toLocaleDateString() : 'Never',
      ]);
      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')),
      ].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `source_mappings_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting mappings:', error);
      alert('Error exporting mappings');
    }
  };

  const hasActiveFilters = Object.values(filters).some(v => v !== 'all');
  const clearFilters = () => setFilters({ source: 'all', confidence: 'all' });

  return (
    <div data-tour="page-source-mappings" className="pt-[30px] pb-24 md:pb-[30px] px-4 md:px-[60px] bg-page-bg min-h-full flex flex-col gap-5">

      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-3">
        <div className="flex flex-col gap-1.5">
          <h1 className="text-[32px] font-medium leading-tight text-page-title">Source Mappings</h1>
          <p className="text-sm font-normal text-page-muted">Map source system fields to canonical data elements</p>
        </div>
        <div className="flex flex-wrap items-center gap-3 mt-1">
          <button
            onClick={exportToCSV}
            className="flex items-center gap-2 w-[183px] h-9 px-4 rounded-[5px] border-2 border-nav-active text-nav-active text-sm font-medium cursor-pointer bg-card-bg hover:border-nav-active-hover hover:text-nav-active-hover transition-colors"
          >
            <ArrowDownCircle size={24} />
            Export CSV
          </button>
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="flex items-center gap-2 w-[183px] h-9 px-4 rounded-[5px] bg-nav-active text-white text-sm font-medium cursor-pointer hover:bg-nav-active-dark transition-colors"
          >
            <PlusCircle size={24} />
            New Mapping
          </button>
        </div>
      </div>

      {/* Search + Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[240px] flex items-center h-9 bg-card-bg rounded-[18px] px-3 gap-2">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" className="shrink-0">
            <path d="M21 21L17.5001 17.5M20 11.5C20 16.1944 16.1944 20 11.5 20C6.80558 20 3 16.1944 3 11.5C3 6.80558 6.80558 3 11.5 3C16.1944 3 20 6.80558 20 11.5Z" stroke="#377AD6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <input
            placeholder="Search mappings..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="flex-1 bg-transparent border-0 outline-none text-sm text-page-text placeholder:text-page-muted"
          />
        </div>
        <div className="flex flex-wrap items-center gap-3 shrink-0 max-w-full">
          <FilterSelect value={filters.source} onValueChange={(v) => setFilters(f => ({ ...f, source: v }))}>
            {sourceItems.map(item => (
              <FilterSelectItem key={item.value} value={item.value}>{item.label}</FilterSelectItem>
            ))}
          </FilterSelect>
          <FilterSelect value={filters.confidence} onValueChange={(v) => setFilters(f => ({ ...f, confidence: v }))}>
            {CONFIDENCE_ITEMS.map(item => (
              <FilterSelectItem key={item.value} value={item.value}>{item.label}</FilterSelectItem>
            ))}
          </FilterSelect>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="flex items-center gap-1 h-9 px-3 rounded-[5px] border-2 border-nav-active text-nav-active text-sm cursor-pointer bg-card-bg hover:border-nav-active-hover hover:text-nav-active-hover transition-colors"
            >
              <X size={14} />
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="flex items-center gap-2">
        <GitBranch size={24} color="#377AD6" />
        <span className="text-sm font-medium text-[#377AD6]">{total.toLocaleString()} mappings</span>
      </div>

      {/* Table */}
      <PaginatedTable
        loadData={loadData}
        quietRefetch
        dependencies={[filters, searchTerm, reloadTrigger]}
        renderTable={(paginatedData, loading) => (
          <div className="bg-card-bg rounded-[8px] overflow-x-auto">
            <MappingsTable
              mappings={paginatedData}
              isLoading={loading}
              onMappingUpdate={triggerReload}
              onEditMapping={openEditModal}
              onDeleteMapping={handleDeleteMapping}
            />
          </div>
        )}
      />

      <CreateMappingModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onMappingCreated={handleMappingUpdated}
        preselectedElementId={location.state?.preselectedElement}
      />

      {editingMapping && (
        <EditMappingModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          onMappingUpdated={handleMappingUpdated}
          mapping={editingMapping}
        />
      )}
    </div>
  );
}
